import React from 'react';

import { CheckCircleOutlined, CheckOutlined, RightOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { DailyWorkflowRuleInfoDTO } from '@recurrency/core-api-schema/dist/dailyWorkflow/getDailyWorkflowTasks';
import { ValidationRuleDTO } from '@recurrency/core-api-schema/dist/validationRules/getValidationRules';
import { theme } from 'theme';

import { FlexSpace } from 'components/FlexSpace';
import { Tooltip } from 'components/Tooltip/Tooltip';

import { WorkflowReviewProgress } from './utils';

export type WorkflowBucket = {
  id: string;
  title: string;
  items: ValidationRuleDTO[];
  color: string;
};

interface DailyWorkflowBucketsProps {
  tasks: DailyWorkflowRuleInfoDTO[];
  reviewProgress: WorkflowReviewProgress;
  selectedRuleId: string;
  setSelectedRuleId: (ruleId: string) => void;
  setSelectedIndex: (index: number) => void;
}

const dailyWorkflowColors = [
  theme.colors.danger[500],
  theme.colors.warning[400],
  theme.colors.warning[300],
  theme.colors.neutral[350],
];

export const getWorkflowBucketColor = (index: number) => dailyWorkflowColors[index % dailyWorkflowColors.length];

export const DailyWorkflowBuckets = ({
  tasks,
  reviewProgress,
  selectedRuleId,
  setSelectedRuleId,
  setSelectedIndex,
}: DailyWorkflowBucketsProps) => (
  <FlexSpace
    wrap
    fullWidth
    gap={0}
    className={css`
      border: 1px solid ${theme.colors.neutral[300]};
      border-radius: 8px;
      border-top-left-radius: 0px;
      border-bottom-left-radius: 0px;
      margin-bottom: 20px;
    `}
  >
    {tasks.map((task, i) => {
      const taskInfo = reviewProgress.items[task.ruleId];
      const isTaskDisabled = taskInfo.totalCount === 0 || taskInfo.areAllItemsSubmitted;
      return (
        <div
          className={css`
            cursor: ${isTaskDisabled ? 'default' : 'pointer'};
            flex: 1;
            border-left: 3px solid ${getWorkflowBucketColor(i)};
            border-bottom: 1px solid ${theme.colors.neutral[300]};

            :first-child {
              border-top-right-radius: 8px;
            }
            :last-child {
              border-bottom: none;
              border-bottom-right-radius: 8px;
            }

            // Selected rule style
            ${selectedRuleId === task.ruleId && `background-color: ${theme.colors.primary[100]};`}

            // Disabled rule style
            ${isTaskDisabled &&
            `
              background-color: ${theme.colors.neutral[75]};
              cursor: default;
              opacity: 0.6;
            `}
          `}
          key={task.ruleId}
          onClick={() => {
            if (isTaskDisabled) return;
            setSelectedRuleId(task.ruleId);
            setSelectedIndex(0);
          }}
        >
          <FlexSpace
            className={css`
              width: 100%;
              padding: 8px 16px;
              justify-content: space-between;
            `}
          >
            <FlexSpace direction="column" gap={0}>
              <FlexSpace
                gap={6}
                alignItems="center"
                className={css`
                  font-size: 16px;
                  font-weight: 700;
                `}
              >
                {task.taskCount} items
                {task.submittedItemLocations.length === task.taskCount ? (
                  <CheckCircleOutlined
                    className={css`
                      font-weight: normal;
                      color: ${theme.colors.success[500]};
                      font-size: 14px;
                      margin-left: 1.5px; // Fix for the 1.5px padding difference from the "○" component below
                    `}
                  />
                ) : (
                  <span
                    className={css`
                      font-weight: 200;
                      font-size: 19px;
                      line-height: 0;
                      color: ${theme.colors.neutral[500]};
                    `}
                  >
                    ○
                  </span>
                )}
              </FlexSpace>
              <Tooltip title={task.ruleDescription} placement="right" mouseEnterDelay={0.05}>
                <div>{task.ruleName}</div>
              </Tooltip>
              <FlexSpace
                gap={8}
                className={css`
                  margin-top: 8px;
                  font-size: 12px;
                `}
              >
                <span
                  className={css`
                    color: ${theme.colors.success[500]};
                  `}
                >
                  <CheckOutlined
                    className={css`
                      margin-right: 4px;
                    `}
                  />
                  {task.submittedItemLocations.length} sent
                </span>
                <span
                  className={css`
                    color: ${theme.colors.warning[600]};
                  `}
                >
                  {taskInfo?.reviewedItemsCount || 0} reviewed
                </span>
                <span
                  className={css`
                    color: ${theme.colors.neutral[500]};
                  `}
                >
                  {taskInfo?.remainingCount || 0} pending
                </span>
              </FlexSpace>
            </FlexSpace>
            <RightOutlined />
          </FlexSpace>
        </div>
      );
    })}
  </FlexSpace>
);
